/**
 * Notification dialog showing all notifications with full details.
 *
 * Features:
 * - Full notification list with date, type, study, status
 * - Pagination for large lists
 * - Mark as read/unread
 * - Archive notifications
 * - Filter by read/unread status
 * - Navigate to related content
 */

import { useState, useEffect } from 'react'
import { format, parseISO } from 'date-fns'
import {
  AlertCircle,
  AlertTriangle,
  Archive,
  Bell,
  Check,
  CheckCircle,
  ChevronRight,
  Clock,
  Download,
  Eye,
  FileDown,
  Filter,
  Key,
  Loader2,
  Megaphone,
  MessageCircle,
  RefreshCw,
  Shield,
  Upload,
  UserCheck,
  UserMinus,
  UserPlus,
  XCircle,
} from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import {
  useNotificationStore,
  Notification,
} from '@/stores/notificationStore'
import { cn } from '@/lib/utils'

interface NotificationDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
}

// Map icon names from backend to Lucide icons
const iconMap: Record<string, React.ComponentType<{ className?: string }>> = {
  Upload: Upload,
  CheckCircle: CheckCircle,
  XCircle: XCircle,
  UserPlus: UserPlus,
  UserMinus: UserMinus,
  Shield: Shield,
  Key: Key,
  Clock: Clock,
  AlertTriangle: AlertTriangle,
  Megaphone: Megaphone,
  AlertCircle: AlertCircle,
  UserCheck: UserCheck,
  RefreshCw: RefreshCw,
  MessageCircle: MessageCircle,
  Download: Download,
  FileDown: FileDown,
  FolderPlus: Upload,
  FolderCog: RefreshCw,
  FolderMinus: XCircle,
  CheckCircle2: CheckCircle,
  UserCog: RefreshCw,
  Lock: Key,
  Unlock: Key,
  Navigation: MessageCircle,
  Blocks: MessageCircle,
  ArrowRightLeft: RefreshCw,
  Camera: Clock,
  ShieldAlert: Shield,
  ShieldCheck: Shield,
  ShieldOff: Shield,
  Users: UserPlus,
  Wrench: RefreshCw,
  Database: Download,
  DatabaseBackup: Download,
}

// Priority badge styling
const priorityBadge: Record<string, { variant: 'default' | 'secondary' | 'destructive' | 'outline'; label: string }> = {
  urgent: { variant: 'destructive', label: 'Urgent' },
  high: { variant: 'destructive', label: 'High' },
  normal: { variant: 'secondary', label: 'Normal' },
  low: { variant: 'outline', label: 'Low' },
}

// Category badge styling
const categoryBadge: Record<string, { className: string; label: string }> = {
  data: { className: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-100', label: 'Data' },
  issues: { className: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-100', label: 'Issues' },
  access: { className: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-100', label: 'Access' },
  security: { className: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-100', label: 'Security' },
  system: { className: 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-100', label: 'System' },
  exports: { className: 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-100', label: 'Exports' },
}

function getTypeCategory(type: string): string {
  // Map notification types to categories
  const categoryMap: Record<string, string> = {
    data_upload_complete: 'data',
    data_upload_failed: 'data',
    data_validation_warning: 'data',
    data_snapshot_created: 'data',
    data_mapping_updated: 'data',
    issue_created: 'issues',
    issue_assigned: 'issues',
    issue_updated: 'issues',
    issue_resolved: 'issues',
    issue_comment: 'issues',
    user_created: 'access',
    user_deleted: 'access',
    user_activated: 'access',
    user_deactivated: 'access',
    user_role_changed: 'access',
    study_access_granted: 'access',
    study_access_revoked: 'access',
    bulk_user_import: 'access',
    account_locked: 'security',
    account_unlocked: 'security',
    failed_login_attempt: 'security',
    mfa_enabled: 'security',
    mfa_disabled: 'security',
    study_created: 'system',
    study_updated: 'system',
    study_deleted: 'system',
    study_published: 'system',
    study_navigation_updated: 'system',
    study_modules_updated: 'system',
    system_maintenance: 'system',
    backup_completed: 'system',
    backup_failed: 'system',
    export_ready: 'exports',
    export_failed: 'exports',
  }
  return categoryMap[type] || 'system'
}

export function NotificationDialog({ open, onOpenChange }: NotificationDialogProps) {
  const notifications = useNotificationStore((state) => state.notifications)
  const unreadCount = useNotificationStore((state) => state.unreadCount)
  const totalCount = useNotificationStore((state) => state.totalCount)
  const isLoading = useNotificationStore((state) => state.isLoading)
  const hasMore = useNotificationStore((state) => state.hasMore)
  const currentPage = useNotificationStore((state) => state.currentPage)
  const fetchNotifications = useNotificationStore((state) => state.fetchNotifications)
  const markAsRead = useNotificationStore((state) => state.markAsRead)
  const markAllAsRead = useNotificationStore((state) => state.markAllAsRead)
  const archiveNotification = useNotificationStore((state) => state.archiveNotification)

  const [filter, setFilter] = useState<'all' | 'unread' | 'read'>('all')

  // Fetch notifications when dialog opens
  useEffect(() => {
    if (open) {
      fetchNotifications(1, true)
    }
  }, [open, fetchNotifications])

  // Filter notifications
  const filteredNotifications = notifications.filter((n) => {
    if (filter === 'unread') return !n.isRead
    if (filter === 'read') return n.isRead
    return true
  })

  const handleRowClick = (notification: Notification) => {
    if (!notification.isRead) {
      markAsRead(notification.id)
    }
  }

  const handleLoadMore = () => {
    if (hasMore && !isLoading) {
      fetchNotifications(currentPage + 1)
    }
  }

  const formatDate = (dateString: string) => {
    try {
      return format(parseISO(dateString), 'MMM d, yyyy h:mm a')
    } catch {
      return dateString
    }
  }

  const getIcon = (notification: Notification) => {
    const Icon = notification.icon ? iconMap[notification.icon] : Bell
    return Icon || Bell
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[85vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Bell className="h-5 w-5" />
            All Notifications
            {unreadCount > 0 && (
              <Badge variant="destructive" className="ml-2">
                {unreadCount} unread
              </Badge>
            )}
          </DialogTitle>
          <DialogDescription>
            View and manage all your notifications. Total: {totalCount} notifications.
          </DialogDescription>
        </DialogHeader>

        {/* Toolbar */}
        <div className="flex items-center justify-between gap-4 py-2 border-b">
          <div className="flex items-center gap-2">
            <Filter className="h-4 w-4 text-muted-foreground" />
            <Select value={filter} onValueChange={(v) => setFilter(v as typeof filter)}>
              <SelectTrigger className="w-32">
                <SelectValue placeholder="Filter" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                <SelectItem value="unread">Unread</SelectItem>
                <SelectItem value="read">Read</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="flex items-center gap-2">
            {unreadCount > 0 && (
              <Button variant="outline" size="sm" onClick={markAllAsRead}>
                <Check className="h-4 w-4 mr-1" />
                Mark all as read
              </Button>
            )}
            <Button
              variant="outline"
              size="sm"
              onClick={() => fetchNotifications(1, true)}
              disabled={isLoading}
            >
              <RefreshCw className={cn('h-4 w-4 mr-1', isLoading && 'animate-spin')} />
              Refresh
            </Button>
          </div>
        </div>

        {/* Notification table */}
        <div className="flex-1 overflow-y-auto -mx-6 px-6" style={{ maxHeight: 'calc(85vh - 200px)' }}>
          {filteredNotifications.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <Bell className="h-12 w-12 text-muted-foreground mb-4" />
              <p className="text-lg font-medium">No notifications</p>
              <p className="text-sm text-muted-foreground">
                {filter === 'unread'
                  ? "You're all caught up!"
                  : filter === 'read'
                  ? 'No read notifications yet.'
                  : 'No notifications to display.'}
              </p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-10"></TableHead>
                  <TableHead className="w-[300px]">Notification</TableHead>
                  <TableHead className="w-24">Category</TableHead>
                  <TableHead className="w-20">Priority</TableHead>
                  <TableHead className="w-24">Study</TableHead>
                  <TableHead className="w-40">Date</TableHead>
                  <TableHead className="w-20 text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredNotifications.map((notification) => {
                  const Icon = getIcon(notification)
                  const category = getTypeCategory(notification.type)
                  const categoryStyle = categoryBadge[category] || categoryBadge.system
                  const priorityStyle = priorityBadge[notification.priority] || priorityBadge.normal

                  return (
                    <TableRow
                      key={notification.id}
                      className={cn(
                        'hover:bg-accent/50',
                        !notification.isRead && 'bg-accent/30 font-medium'
                      )}
                      onClick={() => handleRowClick(notification)}
                    >
                      <TableCell>
                        <div className="flex items-center justify-center">
                          {!notification.isRead && (
                            <span className="h-2 w-2 rounded-full bg-primary" />
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-start gap-3">
                          <Icon className="h-5 w-5 mt-0.5 shrink-0 text-muted-foreground" />
                          <div className="min-w-0">
                            <p className={cn('text-sm', !notification.isRead && 'font-semibold')}>
                              {notification.title}
                            </p>
                            {notification.message && (
                              <p className="text-xs text-muted-foreground line-clamp-1 mt-0.5">
                                {notification.message}
                              </p>
                            )}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge
                          variant="outline"
                          className={cn('text-xs', categoryStyle.className)}
                        >
                          {categoryStyle.label}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Badge variant={priorityStyle.variant} className="text-xs">
                          {priorityStyle.label}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        {notification.studyId ? (
                          <span className="text-xs font-mono">{notification.studyId}</span>
                        ) : (
                          <span className="text-xs text-muted-foreground">—</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <span className="text-xs text-muted-foreground">
                          {formatDate(notification.createdAt)}
                        </span>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          {!notification.isRead && (
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-7 w-7"
                                  onClick={(e) => {
                                    e.stopPropagation()
                                    markAsRead(notification.id)
                                  }}
                                >
                                  <Eye className="h-3.5 w-3.5" />
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent>Mark as read</TooltipContent>
                            </Tooltip>
                          )}
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-7 w-7"
                                onClick={(e) => {
                                  e.stopPropagation()
                                  archiveNotification(notification.id)
                                }}
                              >
                                <Archive className="h-3.5 w-3.5" />
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent>Archive</TooltipContent>
                          </Tooltip>
                        </div>
                      </TableCell>
                    </TableRow>
                  )
                })}
              </TableBody>
            </Table>
          )}
        </div>

        {/* Pagination / Load more */}
        {hasMore && (
          <div className="flex items-center justify-center py-4 border-t">
            <Button
              variant="outline"
              onClick={handleLoadMore}
              disabled={isLoading}
            >
              {isLoading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Loading...
                </>
              ) : (
                <>
                  <ChevronRight className="h-4 w-4 mr-1" />
                  Load More
                </>
              )}
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  )
}
